/**
 * Lazy CodeMirror wrapper for section-scoped Live CSS Code View.
 * Owns editor lifecycle, validation, debounced writes, and undo key routing.
 */

import { assembleSectionOverrideCSS } from '../renderer/css-assembler.js';

const LIVE_EDIT_DEBOUNCE_MS = 250;
const LIVE_EDIT_COMMIT_IDLE_MS = 1500;
const EMPTY_HANDLE = Object.freeze({
  destroy() {},
  getValue() {
    return '';
  },
  setValue() {},
  flush() {},
  commitPending() {},
  focusEnd() {},
  isDestroyed() {
    return true;
  }
});
const INCOMPLETE_CSS_MESSAGE =
  'CSS looks incomplete; preview is paused until input parses cleanly.';

let _cmModulePromise = null;
let _cmImportFn = () => import('../vendor/codemirror/codemirror.esm.js');

export function loadCodeMirror() {
  if (!_cmModulePromise) {
    _cmModulePromise = _cmImportFn();
  }
  return _cmModulePromise;
}

export function __setCodeMirrorImportForTests(fn) {
  _cmImportFn = typeof fn === 'function' ? fn : _cmImportFn;
  _cmModulePromise = null;
}

export function __resetCodeMirrorLoaderForTests() {
  _cmImportFn = () => import('../vendor/codemirror/codemirror.esm.js');
  _cmModulePromise = null;
}

function hasConstructableStylesheet() {
  try {
    return typeof CSSStyleSheet === 'function' && !!new CSSStyleSheet();
  } catch {
    return false;
  }
}

function scanCSSStructure(cssText) {
  let depth = 0;
  let quote = '';
  let escaped = false;
  let inComment = false;
  let hasBrace = false;

  for (let i = 0; i < cssText.length; i++) {
    const ch = cssText[i];
    const next = cssText[i + 1];

    if (inComment) {
      if (ch === '*' && next === '/') {
        inComment = false;
        i++;
      }
      continue;
    }

    if (quote) {
      if (escaped) {
        escaped = false;
      } else if (ch === '\\') {
        escaped = true;
      } else if (ch === quote) {
        quote = '';
      }
      continue;
    }

    if (ch === '/' && next === '*') {
      inComment = true;
      i++;
      continue;
    }

    if (ch === '"' || ch === "'") {
      quote = ch;
      continue;
    }

    if (ch === '{') {
      hasBrace = true;
      depth++;
      continue;
    }

    if (ch === '}') {
      hasBrace = true;
      depth--;
      if (depth < 0) return { balanced: false, hasBrace };
    }
  }

  return {
    balanced: depth === 0 && !quote && !inComment,
    hasBrace
  };
}

function looksLikeBareDeclarations(cssText) {
  const stripped = cssText.replace(/\/\*[\s\S]*?\*\//g, '').trim();
  if (!stripped) return true;
  if (/^[.#[:>+~]/.test(stripped)) return false;
  return /^-{0,2}[a-zA-Z][\w-]*\s*:/.test(stripped);
}

export function validateSectionCSS(cssText) {
  const value = String(cssText || '');
  if (!value.trim()) return { ok: true, css: value };

  const structure = scanCSSStructure(value);
  if (!structure.balanced) {
    return { ok: false, css: value, message: INCOMPLETE_CSS_MESSAGE };
  }

  if (!structure.hasBrace && !looksLikeBareDeclarations(value)) {
    return { ok: false, css: value, message: INCOMPLETE_CSS_MESSAGE };
  }

  if (!hasConstructableStylesheet()) return { ok: true, css: value };

  const sheet = new CSSStyleSheet();
  try {
    const parseText = value.includes('{') ? value : `[data-pb-code-view-validation] { ${value} }`;
    sheet.replaceSync(parseText);
    return { ok: true, css: value };
  } catch (err) {
    return {
      ok: false,
      css: value,
      message: err?.message || INCOMPLETE_CSS_MESSAGE
    };
  }
}

export function buildAppliedCSSText(section) {
  const blocks = [];
  blocks.push('/* -- Section source (read-only) -- */');
  blocks.push(section?.css?.trim() || '/* (none) */');
  blocks.push('');
  blocks.push('/* -- Element-level overrides (Properties tab) -- */');
  blocks.push(assembleSectionOverrideCSS(section, { includeCustomCSS: false }) || '/* (none) */');
  blocks.push('');
  blocks.push('/* -- Custom CSS (editable in the Custom tab) -- */');
  blocks.push(section?.overrides?.customCSS?.trim() || '/* (none) */');
  return blocks.join('\n');
}

function toArray(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.classes)) return value.classes;
  return [];
}

function selectorLooksEditable(docText, pos) {
  const prefix = docText.slice(0, pos);
  const lastOpen = prefix.lastIndexOf('{');
  const lastClose = prefix.lastIndexOf('}');
  if (lastOpen <= lastClose) return true;

  const sinceOpen = prefix.slice(lastOpen + 1);
  const lastColon = sinceOpen.lastIndexOf(':');
  const lastSemi = sinceOpen.lastIndexOf(';');
  const lastNewline = sinceOpen.lastIndexOf('\n');
  return lastColon < Math.max(lastSemi, lastNewline);
}

function varFunctionRange(docText, pos) {
  const prefix = docText.slice(0, pos);
  const start = prefix.lastIndexOf('var(');
  if (start === -1) return null;
  const close = prefix.lastIndexOf(')');
  if (close > start) return null;
  return { from: start + 4 };
}

function createCompletionSource(cm, classIndex, cssVariables) {
  const classes = toArray(classIndex)
    .map((item) => String(item || '').trim())
    .filter((item) => item.startsWith('cs-'));
  const vars = toArray(cssVariables)
    .map((item) => String(item || '').trim())
    .filter((item) => item.startsWith('--'));

  return (context) => {
    const docText = context.state.doc.toString();
    const varRange = varFunctionRange(docText, context.pos);
    if (varRange) {
      const token = context.matchBefore(/--[\w-]*/);
      if (!token && !context.explicit) return null;
      const prefix = token?.text || '';
      const options = vars
        .filter((name) => !prefix || name.startsWith(prefix))
        .slice(0, 60)
        .map((name) => ({ label: name, type: 'variable', apply: name }));
      return options.length ? { from: token?.from ?? varRange.from, options } : null;
    }

    const word = context.matchBefore(/\.[\w-]*/);
    if (!word || (word.from === word.to && !context.explicit)) return null;
    if (!selectorLooksEditable(docText, word.from)) return null;

    const prefix = word.text.slice(1);
    const options = classes
      .filter((name) => !prefix || name.startsWith(prefix))
      .slice(0, 80)
      .map((name) => ({ label: name, type: 'class', apply: name }));
    return options.length ? { from: word.from + 1, options } : null;
  };
}

function createTheme(cm) {
  return cm.EditorView.theme(
    {
      '&': {
        height: '100%',
        minHeight: '220px',
        color: 'var(--pb-text-bright)',
        backgroundColor: 'var(--pb-surface-2)'
      },
      '.cm-scroller': {
        fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
        fontSize: '12px',
        lineHeight: '1.55'
      },
      '.cm-content': {
        padding: '10px 0',
        caretColor: 'var(--pb-accent)'
      },
      '.cm-line': {
        padding: '0 10px'
      },
      '.cm-gutters': {
        backgroundColor: 'var(--pb-surface-2)',
        color: 'var(--pb-text-dim)',
        borderRight: '1px solid var(--pb-border)'
      },
      '.cm-activeLineGutter': {
        backgroundColor: 'rgba(255,255,255,0.05)'
      },
      '&.cm-focused': {
        outline: '1px solid var(--pb-accent)',
        outlineOffset: '-1px'
      },
      '.cm-tooltip': {
        backgroundColor: 'var(--pb-surface)',
        border: '1px solid var(--pb-border)',
        color: 'var(--pb-text)'
      },
      '.cm-tooltip-autocomplete ul li[aria-selected]': {
        backgroundColor: 'var(--pb-accent)',
        color: 'var(--pb-bg)'
      }
    },
    { dark: true }
  );
}

function insertTwoSpaces(cm, view) {
  view.dispatch(
    view.state.changeByRange((range) => ({
      changes: { from: range.from, to: range.to, insert: '  ' },
      range: cm.EditorSelection.cursor(range.from + 2)
    }))
  );
  return true;
}

export async function mountCodeEditor({
  container,
  initialValue = '',
  onChange,
  onTransactionStart,
  onCommit,
  onUndo,
  onRedo,
  onValidityChange,
  classIndex = null,
  cssVariables = [],
  isMountCurrent = null
} = {}) {
  if (!container) return EMPTY_HANDLE;
  const cm = await loadCodeMirror();
  if (!container.isConnected || (typeof isMountCurrent === 'function' && !isMountCurrent())) {
    return EMPTY_HANDLE;
  }

  let view = null;
  let destroyed = false;
  let suppressChange = false;
  let txActive = false;
  let pendingValue = null;
  let debounceTimer = null;
  let idleTimer = null;
  let lastValidity = true;

  const notifyValidity = (ok, message = '') => {
    if (ok === lastValidity && ok) return;
    lastValidity = ok;
    onValidityChange?.({ ok, message });
  };

  const clearDebounce = () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = null;
  };

  const clearIdle = () => {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = null;
  };

  const ensureTransaction = () => {
    if (txActive) return;
    txActive = true;
    onTransactionStart?.();
  };

  const flush = () => {
    clearDebounce();
    if (pendingValue === null || destroyed) return;
    const next = pendingValue;
    pendingValue = null;
    onChange?.(next);
  };

  const commitPending = () => {
    flush();
    clearIdle();
    if (!txActive) return;
    txActive = false;
    onCommit?.();
  };

  const scheduleCommit = () => {
    clearIdle();
    idleTimer = setTimeout(commitPending, LIVE_EDIT_COMMIT_IDLE_MS);
  };

  const scheduleWrite = (value) => {
    pendingValue = value;
    clearDebounce();
    debounceTimer = setTimeout(flush, LIVE_EDIT_DEBOUNCE_MS);
  };

  const handleDocChange = () => {
    if (!view || suppressChange || destroyed) return;
    const value = view.state.doc.toString();
    ensureTransaction();
    scheduleCommit();
    const validity = validateSectionCSS(value);
    if (!validity.ok) {
      clearDebounce();
      pendingValue = null;
      notifyValidity(false, validity.message || INCOMPLETE_CSS_MESSAGE);
      return;
    }
    notifyValidity(true);
    scheduleWrite(value);
  };

  const extensions = [
    cm.lineNumbers(),
    cm.bracketMatching(),
    cm.closeBrackets(),
    cm.syntaxHighlighting(cm.defaultHighlightStyle, { fallback: true }),
    cm.css(),
    cm.autocompletion({
      override: [createCompletionSource(cm, classIndex, cssVariables)]
    }),
    cm.Prec.high(
      cm.keymap.of([
        {
          key: 'Mod-z',
          run() {
            commitPending();
            onUndo?.();
            return true;
          }
        },
        {
          key: 'Mod-Shift-z',
          run() {
            commitPending();
            onRedo?.();
            return true;
          }
        },
        {
          key: 'Ctrl-y',
          run() {
            commitPending();
            onRedo?.();
            return true;
          }
        },
        {
          key: 'Tab',
          run(editorView) {
            return insertTwoSpaces(cm, editorView);
          }
        }
      ])
    ),
    cm.keymap.of([...cm.closeBracketsKeymap, ...cm.completionKeymap, ...cm.defaultKeymap]),
    createTheme(cm),
    cm.EditorView.updateListener.of((update) => {
      if (update.docChanged) handleDocChange();
    })
  ];

  view = new cm.EditorView({
    state: cm.EditorState.create({
      doc: String(initialValue || ''),
      extensions
    }),
    parent: container
  });
  view.dom.addEventListener('blur', commitPending, true);

  return {
    destroy() {
      if (destroyed) return;
      commitPending();
      destroyed = true;
      clearDebounce();
      clearIdle();
      view?.destroy();
      view = null;
    },
    getValue() {
      return view ? view.state.doc.toString() : '';
    },
    setValue(value) {
      if (!view || destroyed) return;
      const next = String(value || '');
      const current = view.state.doc.toString();
      if (current === next) return;
      suppressChange = true;
      view.dispatch({
        changes: { from: 0, to: current.length, insert: next }
      });
      suppressChange = false;
      pendingValue = null;
      clearDebounce();
      notifyValidity(validateSectionCSS(next).ok);
    },
    focusEnd() {
      if (!view || destroyed) return;
      const end = view.state.doc.length;
      view.dispatch({ selection: { anchor: end } });
      view.focus();
    },
    flush,
    commitPending,
    isDestroyed() {
      return destroyed;
    }
  };
}

export async function mountReadOnlyEditor({
  container,
  value = '',
  onSelectionChange,
  isMountCurrent = null
} = {}) {
  if (!container) return EMPTY_HANDLE;
  const cm = await loadCodeMirror();
  if (!container.isConnected || (typeof isMountCurrent === 'function' && !isMountCurrent())) {
    return EMPTY_HANDLE;
  }

  let view = null;
  let destroyed = false;
  let lastHasSelection = null;

  const emitSelectionChange = () => {
    if (!view || destroyed) return;
    const hasSelection = view.state.selection.ranges.some((range) => !range.empty);
    if (hasSelection === lastHasSelection) return;
    lastHasSelection = hasSelection;
    onSelectionChange?.(hasSelection);
  };

  const extensions = [
    cm.lineNumbers(),
    cm.bracketMatching(),
    cm.syntaxHighlighting(cm.defaultHighlightStyle, { fallback: true }),
    cm.css(),
    cm.EditorState.readOnly.of(true),
    cm.EditorView.editable.of(false),
    cm.EditorView.lineWrapping,
    createTheme(cm),
    cm.EditorView.updateListener.of((update) => {
      if (update.selectionSet || update.docChanged) emitSelectionChange();
    })
  ];

  view = new cm.EditorView({
    state: cm.EditorState.create({
      doc: String(value || ''),
      extensions
    }),
    parent: container
  });
  emitSelectionChange();

  return {
    destroy() {
      if (destroyed) return;
      destroyed = true;
      view?.destroy();
      view = null;
    },
    getValue() {
      return view ? view.state.doc.toString() : '';
    },
    setValue(text) {
      if (!view || destroyed) return;
      const next = String(text || '');
      const current = view.state.doc.toString();
      if (current === next) return;
      view.dispatch({
        changes: { from: 0, to: current.length, insert: next },
        selection: { anchor: 0 }
      });
      emitSelectionChange();
    },
    getSelection() {
      if (!view || destroyed) return '';
      const pieces = [];
      for (const range of view.state.selection.ranges) {
        if (!range.empty) pieces.push(view.state.sliceDoc(range.from, range.to));
      }
      return pieces.join('\n');
    },
    isDestroyed() {
      return destroyed;
    }
  };
}
